import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import backIcon from '../../../../../assets/navbar/back.svg'
import FollowButton from '../../../../../components/common/FollowButton'
import {
  FollowAction,
  getFollowersForUser,
  getFollowingForUser,
  getFollowList,
  searchUsers,
  unFollowAction
} from '../../../../../redux/actions/FollowingActions'
import formatDate from '../../../../../utils/formatDate'
import closeIcon from '../../../assets/close.svg'
import searchIcon from '../../../assets/searchIcon.svg'
import ProfileImage from '../ProfileImage'
import {
  ActiveMarker,
  CloseModal,
  DesignersContent,
  FollowModalCenterer,
  FollowModalDimmer,
  FollowModalWrapper,
  Icon,
  NavigationPanel,
  SearchField,
  SearchFieldInput,
  Tab,
  TabsWrapper,
  TimeAgo,
  UserInfo,
  UserLine,
  Username,
  UserProfilePart
} from './FollowListModal.styles'

export const FOLLOW_LIST_ACTIVE_TAB = {
  FOLLOWING: {
    name: 'Following',
    key: 'following'
  },
  FOLLOWERS: {
    name: 'Followers',
    key: 'followers'
  }
}

export const FollowListModal = ({
  userId,
  defaultTab = FOLLOW_LIST_ACTIVE_TAB.FOLLOWING,
  onClose
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600)
  const [activeTab, setActiveTab] = useState(defaultTab)
  const [searchQuery, setSearchQuery] = useState('')
  const currentUserId = useSelector(state => state.profile.me)
  const searchList = useSelector(state => state.follows.search)
  const following = useSelector(state => state.follows.following)
  const followersList = useSelector(state => state.follows.forUser.followers)
  const followingList = useSelector(state => state.follows.forUser.following)
  const dispatch = useDispatch()
  const scrollListRef = useRef(null)
  const router = useRouter()

  let dataSource =
    activeTab === FOLLOW_LIST_ACTIVE_TAB.FOLLOWING
      ? followingList
      : followersList

  if (searchQuery !== '') {
    dataSource = searchList
  }

  useEffect(() => {
    const listener = () => {
      if (window.innerWidth <= 600) {
        setIsMobile(true)
      } else {
        setIsMobile(false)
      }
    }

    window.addEventListener('resize', listener, true)

    return () => {
      window.removeEventListener('resize', listener)
    }
  })

  useEffect(() => {
    dispatch(getFollowList(currentUserId))

    return () => {
      dispatch(getFollowingForUser(currentUserId))
      dispatch(getFollowersForUser(currentUserId))
    }
  }, [dispatch, currentUserId])

  useEffect(() => {
    if (searchQuery) {
      dispatch(searchUsers(searchQuery))

      return
    }

    if (!userId) return

    switch (activeTab) {
      case FOLLOW_LIST_ACTIVE_TAB.FOLLOWING:
        dispatch(getFollowingForUser(userId))
        break
      case FOLLOW_LIST_ACTIVE_TAB.FOLLOWERS:
        dispatch(getFollowersForUser(userId))
        break
      default:
        break
    }
  }, [userId, activeTab, dispatch, searchQuery])

  useEffect(() => {
    const ref = scrollListRef.current

    const listener = event => {
      const el = event.target

      if (
        el.scrollTop !== 0 &&
        el.scrollHeight - el.scrollTop - el.clientHeight < 100 &&
        dataSource.length >= 10
      ) {
        if (searchQuery !== '') {
          dispatch(searchUsers(searchQuery))

          return
        }

        switch (activeTab) {
          case FOLLOW_LIST_ACTIVE_TAB.FOLLOWING:
            dispatch(getFollowingForUser(userId))
            break
          case FOLLOW_LIST_ACTIVE_TAB.FOLLOWERS:
            dispatch(getFollowersForUser(userId))
            break
          default:
            break
        }
      }
    }
    if (ref) ref.addEventListener('scroll', listener, false)

    return () => {
      if (ref) ref.removeEventListener('scroll', listener)
    }
  }, [scrollListRef, dispatch, activeTab, userId, searchQuery, dataSource])

  const onFollow = userId => {
    dispatch(FollowAction(userId))
  }

  const onUnfollow = userId => {
    dispatch(unFollowAction(userId))
  }

  return (
    <>
      <FollowModalDimmer />
      <FollowModalCenterer onClick={onClose}>
        <FollowModalWrapper onClick={e => e.stopPropagation()}>
          <CloseModal
            onClick={onClose}
            src={isMobile ? backIcon : closeIcon}
            alt="Close modal"
          />
          <NavigationPanel>
            <TabsWrapper visible>
              {Object.values(FOLLOW_LIST_ACTIVE_TAB).map(tab => (
                <Tab
                  key={tab.key}
                  onClick={() => setActiveTab(tab)}
                  active={activeTab === tab}
                >
                  {tab.name}
                  <ActiveMarker />
                </Tab>
              ))}
            </TabsWrapper>
          </NavigationPanel>
          <DesignersContent ref={scrollListRef}>
            <SearchField>
              <SearchFieldInput
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search for users"
              />
              <Icon src={searchIcon} alt="search" />
            </SearchField>
            {dataSource.map((item, i) => {
              let user = item

              if (item.user) {
                user = item.user
              }

              const name = user.uniqueDisplayName || user.username
              const isFollowed = following.includes(user.objectId)

              return (
                <UserLine>
                  <UserProfilePart>
                    <ProfileImage
                      clickable
                      onClick={() => {
                        router.push(`/dm/${user.objectId}`)
                        onClose()
                      }}
                      small={isMobile}
                      user={user}
                      userLevel={user.level}
                      size={64}
                    />
                    <UserInfo>
                      <Username>{name}</Username>
                      <TimeAgo>{formatDate(item.createdAt)}</TimeAgo>
                    </UserInfo>
                  </UserProfilePart>
                  <FollowButton
                    isFollowed={isFollowed}
                    onClick={() => {
                      isFollowed
                        ? onUnfollow(user.objectId)
                        : onFollow(user.objectId)
                    }}
                  />
                </UserLine>
              )
            })}
          </DesignersContent>
        </FollowModalWrapper>
      </FollowModalCenterer>
    </>
  )
}
